import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { deleteMedia, fetchMedia, uploadMedia, type Attachable } from "@/services/media";
import { useAuth } from "@/contexts/AuthContext";
import { useFormat } from "@/lib/format";
import { failText, jobMediaFailText, mediaFailText } from "@/lib/failText";
import { MAX_MB, prepareJobFile } from "@/lib/jobMedia";
import { Toast, useToast } from "@/components/Toast";
import { camOk, isDesk } from "@/lib/device";
import CameraDialog from "@/components/CameraDialog";
import TabletWait from "@/components/TabletWait";
import { askTablet, type CameraRequest } from "@/services/cameraRequests";
import { withdrawMediaRun } from "@/services/preparation";
import { useReadOnly } from "@/components/ReadOnly";

// The photos and videos of one record, and the button that adds more. A tap opens the full file.
// First used on a stock piece (requested-by: Harish, 2026-10-27 — "start on the photo uploader …
// show me the first screen").
// A photo of a stock piece is captioned with what it shows (his gallery); `extra` adds a picture
// that is not one of the record's own files — the catalogue photo.
// `job` is his assembly media block (Z Furniture `media`): its own title and hint, three buttons —
// take a photo, film, choose a file — photos made a 1920 px JPEG before they go up, and under each
// file who added it, when, and its size; a video plays where it is.
// `itemKey` (jobs only): a line's own files, or null for the job's own; left out, every file.
export default function MediaStrip({ type, id, extra, job = false, itemKey, tabletLabel, ensure, eventId, fromOrders, single }: {
  // `id` null: the record does not exist yet, and `ensure` makes it with the first file.
  type: Attachable; id: number | null; extra?: { url: string; caption: string } | null; job?: boolean; itemKey?: string | null;
  // His request label for the tablet, "client — what" (a job's photos only).
  tabletLabel?: string;
  // His photos exist from the first moment: a job not started makes its run on the first file only.
  ensure?: () => Promise<number>;
  // The job's planning entry and the list shown: before its run exists, a tablet is asked for the entry
  // (his camTablet makes nothing), and the run is made by the first photo the tablet sends, in that list.
  eventId?: number | null; fromOrders?: boolean;
  // His single "📷 Photo of the delivery note or boxes" (`docsHtml`): this one button under the photos,
  // and nothing else — no strip of three, no "No photos yet".
  single?: string;
}) {
  const { t } = useT();
  // One clock for a job's times — issues, Slack and these captions — the job's warehouse's own.
  const { stamp } = useFormat();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const photo = useRef<HTMLInputElement>(null);
  const film = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // His PC camera and "📱 Use the tablet", for a job's photos on a PC.
  const [camera, setCamera] = useState(false);
  const [tablet, setTablet] = useState<CameraRequest | null>(null);
  const live = job && type === "run" && isDesk() && camOk();
  // A job's files — its own, a line's, an issue's — are made ready and checked as his `addMedia` does.
  const jobFiles = type === "run" || type === "issue";
  // His read-only viewer opens every photo and video, but gets no button that adds one (index.html:868,
  // 873, 1182): hidden, not greyed.
  const readOnly = useReadOnly("preparation") && jobFiles;
  const [toastMsg, toast] = useToast();
  // His editors' delete (editors of his page are our admins): a first tap arms it ("Confirm delete"),
  // a second within 6 s deletes.
  const { staff } = useAuth();
  const [arming, setArming] = useState<number | null>(null);
  const remove = async (fileId: number) => {
    if (arming !== fileId) { setArming(fileId); window.setTimeout(() => setArming((a) => (a === fileId ? null : a)), 6000); return; }
    setArming(null);
    // His delete goes through `enqueue`, whose failure is his `e.write` — a toast that goes: one that
    // works next clears the line.
    try { await deleteMedia(fileId); setError(null); } catch (e) { setError(mediaFailText(t, e, "job.writeFailed")); }
    // Every list of this record: the job's own, its lines', and all of them together.
    void qc.invalidateQueries({ queryKey: ["media", type, id] });
  };
  const key = itemKey === undefined ? ["media", type, id] : ["media", type, id, itemKey ?? "job"];
  const media = useQuery({ queryKey: key, queryFn: () => fetchMedia(type, id!, itemKey), enabled: id != null });
  // The record the files go to: this one, or the one `ensure` makes for the first file — once, however
  // many shots the camera sends before it answers.
  const making = useRef<Promise<number> | null>(null);
  const target = async () => id ?? (ensure ? await (making.current ??= ensure().catch((e) => { making.current = null; throw e; })) : null);

  const add = async (files: FileList | File[] | null) => {
    if (!files?.length) return;
    setError(null);
    let to: number | null = null;
    // The run `ensure` made for this batch (a job not started) while it holds none of its files yet.
    let made = false, undone = false;
    for (const original of Array.from(files)) {
      setUploading((n) => n + 1);
      try {
        let file = original;
        // His `addMedia`: the photo prepared, the type and the weight checked before anything goes up —
        // and before the run is made, so a refused file makes nothing.
        if (jobFiles) {
          setPreparing(true);
          try { file = await prepareJobFile(original); } finally { setPreparing(false); }
        }
        if (to == null) { to = await target(); made = id == null && to != null; }
        if (to == null) continue;
        await uploadMedia(type, to, file, undefined, itemKey);
        made = false;
        // His toast after each file: "Saved".
        if (job) toast(t("mediaJob.saved"));
      } catch (e) {
        // His `addMedia` catch: his own sentence alone for every failure, by default `vidErr.fail` — never
        // the browser's "Failed to fetch" / "Load failed" (a POST or an S3 PUT the network dropped), and
        // for a job's file never a status, the storage's or the server's (jobMediaFailText).
        const video = /^video\//i.test(original.type) || /\.(mp4|mov|m4v|webm)$/i.test(original.name);
        setError(jobFiles
          ? jobMediaFailText(t, e, { video, mb: Math.ceil(original.size / 1_048_576), max: MAX_MB[video ? "video" : "image"] })
          : mediaFailText(t, e, video ? "media.failVideo" : "media.failPhoto"));
        // His `mutate` writes the job only once a file is up: a run made for a first file that failed
        // holds nothing. The server takes it back with the file's pending row (uploadMedia), or — the row
        // itself never written — when asked here, while it still holds nothing (the sweep finishes a call
        // that cannot get through); the next file asks for one again, and the sheet is read again without it.
        if (made) {
          if (eventId != null && to != null) await withdrawMediaRun(eventId, to).catch(() => {});
          making.current = null; to = null; made = false; undone = true;
        }
      } finally {
        setUploading((n) => n - 1);
      }
    }
    for (const r of [input, photo, film]) if (r.current) r.current.value = "";
    // Every list of this record: the job's own, its lines', and all of them together.
    if (to != null) void qc.invalidateQueries({ queryKey: ["media", type, to] });
    if (undone && eventId != null) void qc.invalidateQueries({ queryKey: ["prep-event", eventId] });
  };
  // His `camTablet` only names the job: the request names the run, or — for a job not started — its
  // planning entry, so a tablet that never answers leaves no empty run behind.
  const askForTablet = async () => {
    setCamera(false);
    const label = tabletLabel ?? t("st.gen");
    try {
      if (id == null && eventId != null) setTablet(await askTablet({ mode: "media", event_id: eventId, from_orders: fromOrders, item_key: itemKey ?? null, label }));
      else {
        const run = await target();
        if (run != null) setTablet(await askTablet({ mode: "media", run_id: run, item_key: itemKey ?? null, label }));
      }
    } catch (e) {
      // His `camTablet`: a request not made says his `cam.e.other`.
      setError(mediaFailText(t, e, "cam.e.other"));
    }
  };
  // The tablet's photos arrived: their run's files are read again — and, the first time, the job's own
  // sheet, which now has the run the photo made.
  const tabletPhotos = (r: CameraRequest) => {
    const run = r.run_id ?? id;
    if (run != null) void qc.invalidateQueries({ queryKey: ["media", type, run] });
    if (id == null && eventId != null) void qc.invalidateQueries({ queryKey: ["prep-event", eventId] });
  };
  // His `mphoto`: the live camera on a desk computer that has one, else the photo input (the camera on
  // a tablet or phone).
  const takePhoto = () => (live ? setCamera(true) : photo.current?.click());

  const buttons = job && !readOnly && (
    <div className="flex flex-wrap gap-2">
      {single ? (
        <button type="button" onClick={takePhoto} disabled={uploading > 0}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:opacity-60">{single}</button>
      ) : ([["mediaJob.photo", photo], ["mediaJob.video", film], ["mediaJob.file", input]] as const).map(([label, ref]) => (
        <button key={label} type="button" onClick={() => (label === "mediaJob.photo" ? takePhoto() : ref.current?.click())} disabled={uploading > 0}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:opacity-60">{t(label)}</button>
      ))}
      <input ref={photo} type="file" accept="image/*" capture="environment" className="hidden" aria-label={single ?? t("mediaJob.photo")} onChange={(e) => void add(e.target.files)} />
      {camera && (
        // His one #toast: "Photo added ✓" at the shot, its text then replaced by "Saved" when it is up.
        <CameraDialog mode="media" onClose={() => setCamera(false)} say={toast}
          onPhoto={(f) => void add([f])}
          // His camera's "Choose a file instead": the photo input for his delivery-note photo.
          onFile={() => { setCamera(false); (single ? photo : input).current?.click(); }}
          onTablet={() => void askForTablet()} />
      )}
      {tablet && <TabletWait request={tablet} onClose={() => setTablet(null)} onPhotos={tabletPhotos} />}
      <input ref={film} type="file" accept="video/*" capture="environment" className="hidden" aria-label={t("mediaJob.video")} onChange={(e) => void add(e.target.files)} />
    </div>
  );

  const list = media.data?.media ?? [];
  // His `videoHtml` and `openMedia` (index.html:874, 1043): title, hint, the files or "No photos yet",
  // the status line, and the buttons last — as his `docsHtml` does for its one button.
  return (
    <div className="space-y-2">
      {/* One line's files sit in its own dialog, which already names them. */}
      {typeof itemKey !== "string" && <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{t(job ? "mediaJob.title" : "media.title")}</p>}
      {job && itemKey === null && <p className="text-xs text-slate-500">{t("mediaJob.hint")}</p>}
      {(list.length > 0 || !!extra || (!job && !readOnly)) && <div className="flex flex-wrap gap-2">
        {list.map((m) => (
          <figure key={m.id} className={job ? "w-40" : "w-20"}>
            {job && m.kind === "video" && m.url ? (
              <video src={m.url} controls preload="metadata" playsInline className="h-28 w-40 rounded-lg bg-black ring-1 ring-slate-300" />
            ) : (
              <a href={m.url ?? undefined} target="_blank" rel="noreferrer" title={m.original_filename ?? ""}
                 className={`block overflow-hidden rounded-lg bg-slate-200 ring-1 ring-slate-300 ${job ? "h-28 w-40" : "h-20 w-20"}`}>
                {m.kind === "image" && (m.thumb_url || m.url)
                  ? <img src={m.thumb_url ?? m.url ?? ""} alt={m.role ? t(`media.role.${m.role}`) : m.original_filename ?? ""} className="h-full w-full object-cover" />
                  : <span className="flex h-full items-center justify-center text-xs text-slate-600">▶ {t("media.video")}</span>}
              </a>
            )}
            {m.role && <figcaption className="mt-0.5 text-center text-[11px] leading-tight text-slate-500">{t(`media.role.${m.role}`)}</figcaption>}
            {/* His `gridHtml` caption: 📷 or 🎥, then who · when · its size in MB to one decimal. */}
            {job && (
              <figcaption className="mt-0.5 text-[11px] leading-tight text-slate-500">
                {m.kind === "image" ? "📷" : "🎥"} {[m.uploaded_by, stamp(m.uploaded_at), m.byte_size ? `${(m.byte_size / 1048576).toFixed(1)} ${t("media.mb")}` : ""].filter(Boolean).join(" · ")}
              </figcaption>
            )}
            {job && staff?.is_admin && (
              <button type="button" onClick={() => void remove(m.id)}
                className={`mt-0.5 rounded px-1.5 text-[11px] ${arming === m.id ? "bg-red-600 font-semibold text-white" : "text-red-700 underline"}`}>
                {arming === m.id ? t("media.delOk") : t("media.del")}
              </button>
            )}
          </figure>
        ))}
        {extra && (
          <figure className="w-20">
            <a href={extra.url} target="_blank" rel="noreferrer" className="block h-20 w-20 overflow-hidden rounded-lg bg-white ring-1 ring-slate-300">
              <img src={extra.url} alt={extra.caption} className="h-full w-full object-contain" />
            </a>
            <figcaption className="mt-0.5 text-center text-[11px] leading-tight text-slate-500">{extra.caption}</figcaption>
          </figure>
        )}
        {!job && !readOnly && (
          <button type="button" onClick={() => input.current?.click()} disabled={uploading > 0}
                  className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed border-slate-400 bg-white px-2 text-center text-xs text-slate-700 disabled:opacity-60">
            {uploading > 0 ? t("media.uploading") : `+ ${t("media.add")}`}
          </button>
        )}
      </div>}
      <input ref={input} type="file" accept="image/*,video/*" multiple className="hidden"
             aria-label={t("media.add")} onChange={(e) => void add(e.target.files)} />
      {/* A list that could not be read is not empty: only its error says anything then. */}
      {/* His job block says "No photo or video yet." (`media.none`); a stock piece's, its photos. */}
      {list.length === 0 && !media.isLoading && !media.error && !single && <p className="text-xs text-slate-400">{t(job ? "mediaJob.empty" : "media.empty")}</p>}
      {/* His `mstat`: what is being sent, or why it was not. */}
      {job && uploading > 0 && <p className="text-xs text-slate-500">{preparing ? t("mediaJob.prep") : t("media.uploading")}</p>}
      {(error || media.error) && <p className="text-xs text-red-600">{error ?? failText(t, media.error, "error.loadFailed")}</p>}
      {buttons}
      {/* Last, over the camera opened from here; it takes no room and lets a tap through. */}
      <div className="pointer-events-none contents"><Toast message={toastMsg} /></div>
    </div>
  );
}
